import { AbortableApiPromise, ApiPromise, ErrorOrFailStatusCode, HttpResponse, HttpVerb } from '../@types';
import {
	sonarHttpDelete,
	sonarHttpGet,
	sonarHttpGetBlob,
	sonarHttpGetStream,
	SonarHttpOptions,
	sonarHttpPatch,
	sonarHttpPost,
	sonarHttpPut
} from './sonarFetch';

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
function isFunction(fn?: unknown): fn is Function {
	return typeof fn === 'function';
}

export interface EndpointCallOptions {
	signal?: AbortSignal;
}

export type UriSource<TRequest = void> = string | ((request: TRequest) => string);
export type ApiResponse = Record<string, unknown> | unknown[] | Blob | string | void | boolean;
export type HeaderSource<TRequest = void> =
	| Record<string, string | undefined>
	| ((request: TRequest) => Record<string, string | undefined>);

export type ApiEndpointHandler<TRequest = void, TResponse extends ApiResponse = void> = (
	request: TRequest,
	options?: EndpointCallOptions
) => ApiPromise<TResponse>;

export type AbortableApiEndpointHandler<TRequest = void, TResponse extends ApiResponse = void> = (
	request: TRequest,
	options?: EndpointCallOptions
) => AbortableApiPromise<TResponse>;

export type ApiStreamEndpointHandler<TRequest = void> = (
	request: TRequest,
	options?: EndpointCallOptions
) => ApiPromise<ReadableStreamDefaultReader<Uint8Array<ArrayBufferLike>>>;

export interface EndpointOptions<TRequest = void> {
	headers?: HeaderSource<TRequest>;
	requestTransform?: (request: TRequest) => unknown;
	responseIsBlob?: boolean;
	skipAuthRefresh?: boolean;
}

export function createEndpoint<TRequest = void, TResponse extends ApiResponse = void>(
	action: HttpVerb,
	uri: UriSource<TRequest>,
	options?: EndpointOptions<TRequest>
) {
	return function post(request: TRequest, postOptions?: EndpointCallOptions): ApiPromise<TResponse> {
		const targetUri = isFunction(uri) ? uri(request) : uri;
		const headers = isFunction(options?.headers) ? options?.headers(request) : options?.headers;
		const requestBody = isFunction(options?.requestTransform) ? options?.requestTransform(request) : request;

		type CalculatedRequestType = typeof requestBody;

		const requestOptions: SonarHttpOptions<CalculatedRequestType> = {
			headers,
			requestBody,
			signal: postOptions?.signal,
			skipAuthRefresh: options?.skipAuthRefresh,
			targetUri
		};

		switch (action) {
			case HttpVerb.Delete:
				return sonarHttpDelete<CalculatedRequestType, TResponse>(requestOptions);
			case HttpVerb.Get:
				return options?.responseIsBlob ?
					sonarHttpGetBlob<CalculatedRequestType>(requestOptions) as ApiPromise<TResponse> :
					sonarHttpGet<CalculatedRequestType, TResponse>(requestOptions);
			case HttpVerb.Patch:
				return sonarHttpPatch<CalculatedRequestType, TResponse>(requestOptions);
			case HttpVerb.Post:
				return sonarHttpPost<CalculatedRequestType, TResponse>(requestOptions);
			case HttpVerb.Put:
				return sonarHttpPut<CalculatedRequestType, TResponse>(requestOptions);
			default:
				throw new Error('Invalid Http Verb');
		}
	};
}

export function createStreamEndpoint<TRequest = void>(
	action: HttpVerb,
	uri: UriSource<TRequest>,
	options?: EndpointOptions<TRequest>
): ApiStreamEndpointHandler<TRequest> {
	return function post(
		request: TRequest,
		postOptions?: EndpointCallOptions
	): ApiPromise<ReadableStreamDefaultReader<Uint8Array<ArrayBufferLike>>> {
		const targetUri = isFunction(uri) ? uri(request) : uri;
		const headers = isFunction(options?.headers) ? options?.headers(request) : options?.headers;
		const requestBody = isFunction(options?.requestTransform) ? options?.requestTransform(request) : request;

		type CalculatedRequestType = typeof requestBody;

		const requestOptions: SonarHttpOptions<CalculatedRequestType> = {
			headers,
			requestBody,
			signal: postOptions?.signal,
			skipAuthRefresh: options?.skipAuthRefresh,
			targetUri
		};

		switch (action) {
			case HttpVerb.Get:
				return sonarHttpGetStream<CalculatedRequestType>(requestOptions);
			default:
				throw new Error('Unsupported Http Verb');
		}
	};
}

export function createDeleteEndpoint<TRequest = void, TResponse extends ApiResponse = void>(
	uri: UriSource<TRequest>,
	options?: EndpointOptions<TRequest>
): ApiEndpointHandler<TRequest, TResponse> {
	return createEndpoint<TRequest, TResponse>(HttpVerb.Delete, uri, options);
}

export function createGetEndpoint<TRequest = void, TResponse extends ApiResponse = void>(
	uri: UriSource<TRequest>,
	options?: EndpointOptions<TRequest>
): ApiEndpointHandler<TRequest, TResponse> {
	return createEndpoint<TRequest, TResponse>(HttpVerb.Get, uri, options);
}

export function createPatchEndpoint<TRequest = void, TResponse extends ApiResponse = void>(
	uri: UriSource<TRequest>,
	options?: EndpointOptions<TRequest>
): ApiEndpointHandler<TRequest, TResponse> {
	return createEndpoint<TRequest, TResponse>(HttpVerb.Patch, uri, options);
}

export function createPutEndpoint<TRequest = void, TResponse extends ApiResponse = void>(
	uri: UriSource<TRequest>,
	options?: EndpointOptions<TRequest>
): ApiEndpointHandler<TRequest, TResponse> {
	return createEndpoint<TRequest, TResponse>(HttpVerb.Put, uri, options);
}

export function createPostEndpoint<TRequest = void, TResponse extends ApiResponse = void>(
	uri: UriSource<TRequest>,
	options?: EndpointOptions<TRequest>
): ApiEndpointHandler<TRequest, TResponse> {
	return createEndpoint<TRequest, TResponse>(HttpVerb.Post, uri, options);
}

export function createGetStreamEndpoint<TRequest = void>(
	uri: UriSource<TRequest>,
	options?: EndpointOptions<TRequest>
): ApiStreamEndpointHandler<TRequest> {
	return createStreamEndpoint(HttpVerb.Get, uri, options);
}

export function makeAbortableEndpoint<TRequest = void, TResponse extends ApiResponse = void>(
	endpoint: ApiEndpointHandler<TRequest, TResponse>
): AbortableApiEndpointHandler<TRequest, TResponse> {
	return function call(request: TRequest, options?: EndpointCallOptions) {
		const abortController = new AbortController();

		const responsePromise = endpoint(request, {
			...options,
			signal: abortController.signal
		}).catch((ex) => {
			if (ex.name === 'AbortError') {
				const cancelResult: HttpResponse<TResponse> = {
					headers: new Headers(),
					status: ErrorOrFailStatusCode.ClientClosedRequest
				};

				return cancelResult;
			}

			throw ex;
		}) as AbortableApiPromise<TResponse>;

		responsePromise.abort = () => abortController.abort();

		return responsePromise;
	};
}
